import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bars3Icon, BellIcon, UserCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth.store'
import { authApi } from '@/api/auth'
import { profileApi } from '@/api/profile'
import { mediaApi } from '@/api/media'
import { notificationsApi, notificationLink } from '@/api/notifications'
import type { InAppNotification } from '@/api/notifications'


interface Props { onMenuClick: () => void }

function timeAgo(iso: string, t: (k: string, opts?: object) => string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60) return t('notifications.justNow')
  if (diff < 3600) return t('notifications.minutesAgo', { n: Math.floor(diff / 60) })
  if (diff < 86400) return t('notifications.hoursAgo', { n: Math.floor(diff / 3600) })
  return t('notifications.daysAgo', { n: Math.floor(diff / 86400) })
}

export default function Navbar({ onMenuClick }: Props) {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)

  const { data: profile } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: profileApi.getMyProfile,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const { data: avatarAsset } = useQuery({
    queryKey: ['media', 'asset', profile?.avatarKey],
    queryFn: () => mediaApi.getPublic(profile!.avatarKey!),
    enabled: !!profile?.avatarKey,
    staleTime: 30 * 60_000,
    retry: false,
  })
  const avatarUrl = avatarAsset?.thumbUrl ?? avatarAsset?.viewUrl
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifications, setNotifications] = useState<InAppNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [notifLoading, setNotifLoading] = useState(false)
  const notifRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Poll unread count every 60 seconds
  useEffect(() => {
    let cancelled = false
    const fetchCount = async () => {
      try {
        const data = await notificationsApi.inbox({ limit: 1 })
        if (!cancelled) setUnreadCount(data.unreadCount)
      } catch { /* ignore — user may not have notifications service */ }
    }
    fetchCount()
    const id = setInterval(fetchCount, 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  // Load full list when panel opens
  useEffect(() => {
    if (!notifOpen) return
    setNotifLoading(true)
    notificationsApi.inbox({ limit: 20 })
      .then(data => {
        setNotifications(data.items)
        setUnreadCount(data.unreadCount)
      })
      .catch(() => {})
      .finally(() => setNotifLoading(false))
  }, [notifOpen])

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false)
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  async function handleLogout() {
    await authApi.logout()
    logout()
  }

  async function handleMarkAllRead() {
    await notificationsApi.markAllRead().catch(() => {})
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
    setUnreadCount(0)
  }

  async function handleMarkRead(id: string) {
    await notificationsApi.markRead(id).catch(() => {})
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n))
    setUnreadCount(prev => Math.max(0, prev - 1))
  }

  const lang = i18n.language === 'it' ? 'it' : 'en'
  const [searchQ, setSearchQ] = useState('')

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    const q = searchQ.trim()
    if (!q) return
    navigate(`/app/search?q=${encodeURIComponent(q)}`)
    setSearchQ('')
  }, [searchQ, navigate])

  return (
    <header className="h-14 flex-shrink-0 bg-white border-b border-gray-200 flex items-center px-4 gap-3">
      <button
        className="lg:hidden p-2.5 -ml-1 rounded-xl text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
        onClick={onMenuClick}
        aria-label={t('auto.componentsNavbar.menu')}
      >
        <Bars3Icon className="w-5 h-5" />
      </button>

      {/* Global search */}
      <form onSubmit={handleSearch} className="hidden sm:flex items-center gap-1 bg-gray-100 rounded-lg px-3 py-1.5 w-52 lg:w-64">
        <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          type="search"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder={t('nav.searchPlaceholder')}
          className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full"
        />
      </form>

      <div className="flex-1" />

      {/* Language toggle */}
      <LanguageSwitcher />

      {/* Notifications */}
      <div className="relative" ref={notifRef}>
        <button
          onClick={() => setNotifOpen(v => !v)}
          className="relative p-2.5 rounded-xl text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
          aria-label={t('notifications.title')}
        >
          <BellIcon className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {notifOpen && (
          <div className="absolute right-0 top-full mt-1 w-[calc(100vw-2rem)] max-w-sm sm:w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-800">{t('notifications.title')}</span>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-96 overflow-y-auto">
              {notifLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                  <BellIcon className="w-8 h-8 text-gray-300 mb-2" />
                  <p className="text-sm font-medium text-gray-500">{t('notifications.noNotifications')}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t('notifications.noNotificationsDesc')}</p>
                </div>
              ) : (
                notifications.map(n => {
                  const link = notificationLink(n)
                  return (
                    <button
                      key={n.id}
                      onClick={() => {
                        if (!n.isRead) handleMarkRead(n.id)
                        if (link) { setNotifOpen(false); navigate(link) }
                      }}
                      className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors ${!n.isRead ? 'bg-blue-50/60' : ''} ${link ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.isRead && (
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                        <div className={!n.isRead ? '' : 'pl-4'}>
                          <p className="text-sm font-medium text-gray-800 leading-snug">
                            {lang === 'it' ? n.titleIt : n.titleEn}
                          </p>
                          {(n.bodyIt || n.bodyEn) && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                              {lang === 'it' ? n.bodyIt : n.bodyEn}
                            </p>
                          )}
                          <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.createdAt, (k, opts) => t(k, opts as Record<string, unknown>))}</p>
                        </div>
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* User menu */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(v => !v)}
          data-testid="user-menu"
          className="flex items-center gap-2 p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover ring-1 ring-gray-200" />
          ) : (
            <UserCircleIcon className="w-7 h-7 text-gray-600" />
          )}
          <span className="hidden sm:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
            {profile?.displayName ?? profile?.firstName ?? user?.email?.split('@')[0]}
          </span>
        </button>

        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50">
            <Link
              to="/app/profile"
              onClick={() => setMenuOpen(false)}
              className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100"
            >
              {t('nav.profile')}
            </Link>
            <Link
              to="/app/medical?tab=fse"
              onClick={() => setMenuOpen(false)}
              className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100"
            >
              {t('nav.medicalRecord')}
            </Link>
            <Link
              to="/app/settings"
              onClick={() => setMenuOpen(false)}
              className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100"
            >
              {t('nav.settings')}
            </Link>
            <Link
              to="/app/manuali"
              onClick={() => setMenuOpen(false)}
              className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100"
            >
              {t('nav.manuals', 'Manuali')}
            </Link>
            <hr className="my-1 border-gray-100" />
            <button
              onClick={handleLogout}
              className="w-full text-left flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 active:bg-red-100"
            >
              {t('auth.logout')}
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
